import { useRefs } from 'contexts/RefContext';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from '../../util';
import useScreenDims from '../useScreenDims';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { BREAK_POINT_FOR_MOBILE_VIEW, STANDARD_IMAGERY_SIZE } from '../../constants';

const useKonvaStageDimensions = ({ stageWrapRef }: { stageWrapRef: MutableRefObject<HTMLDivElement | null> }): Dimensions => {
	const { konvaRef, konvaBottomDivRef } = useRefs();
	const screenDims = useScreenDims();
	const { imgHeight, imgWidth, org } = useSelector((state: RootState) => state.roofData.data);
	const { fullScreenEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);

	// maintains aspect ratio as well
	const [stageDims, setStageDims] = useState<Dimensions>({ height: imgHeight, width: imgWidth });

	const setCanvasDimensionsBasedOnScreen = useCallback(() => {
		const stageWrap = stageWrapRef?.current;
		const stage = konvaRef?.current;

		if (!stageWrap || !stage) return;

		const imageRatio = imgWidth / imgHeight;
		const { width: screenWidth, height: screenHeight } = screenDims;
		const stretchImagery = org.setting.isB2C;
		const isDesktopView = screenWidth >= BREAK_POINT_FOR_MOBILE_VIEW;
		const isMobileView = 200 < screenWidth && screenWidth < 500;
		const rectTop = stageWrap.getBoundingClientRect().top;
		const rectB = stageWrap.getBoundingClientRect().bottom;

		let width = screenWidth,
			height = STANDARD_IMAGERY_SIZE;

		if (stretchImagery) {
			if (isDesktopView){
				width = stageWrap.clientWidth;
				height = screenHeight - rectTop;
			} 
			if (isMobileView) {
				width = screenWidth;
				height = STANDARD_IMAGERY_SIZE;
			}
		} else {
			if (isDesktopView && stageWrap.clientWidth > STANDARD_IMAGERY_SIZE) {
				width = fullScreenEnabled ? screenWidth : stageWrap.clientWidth;
				height = fullScreenEnabled ? screenHeight - rectTop : width / imageRatio;
			} else {
				height = screenHeight - (rectTop + (konvaBottomDivRef?.current?.getBoundingClientRect()?.height || 0));
				// height = screenWidth/ imageRatio;
			}
		}
		// eslint-disable-next-line max-len
		// console.log('konvaZoomScale', rectTop, rectB, height, screenHeight, stageWrap.clientHeight, window.innerHeight, stageWrap,width / imageRatio, konvaBottomDivRef?.current?.getBoundingClientRect());
		stage.width(width);
		stage.height(height);
		// keep the same object when the size is unchanged: it feeds ToolDimensionsAndScaleContext, and a new
		// object re-rendered every panel, label and facet even though nothing changed size
		setStageDims((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
	}, [stageWrapRef, konvaRef, imgWidth, imgHeight, screenDims, org.setting.isB2C, konvaBottomDivRef, fullScreenEnabled]);

	const debouncedUpdateCanvasDimensions = useMemo(
		() => debounce(setCanvasDimensionsBasedOnScreen),
		[setCanvasDimensionsBasedOnScreen]);

	useEffect(() => {
		debouncedUpdateCanvasDimensions();
	}, [debouncedUpdateCanvasDimensions]);

	return stageDims;
};

export default useKonvaStageDimensions;
